import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X, Package } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface Package {
	name: string;
	description: string;
	price: string;
	discountPercentage: string;
}

interface PackagesStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const PackagesStep = ({
	onNext,
	onBack,
	initialData,
}: PackagesStepProps) => {
	const [packages, setPackages] = useState<Package[]>(
		initialData?.packages || [
			{ name: "", description: "", price: "0.00", discountPercentage: "0" },
		],
	);
	const [loading, setLoading] = useState(false);

	const addPackage = () => {
		setPackages([
			...packages,
			{ name: "", description: "", price: "0.00", discountPercentage: "0" },
		]);
	};

	const removePackage = (index: number) => {
		if (packages.length > 1) {
			setPackages(packages.filter((_, i) => i !== index));
		}
	};

	const updatePackage = (
		index: number,
		field: keyof Package,
		value: string,
	) => {
		const updated = [...packages];
		updated[index] = { ...updated[index], [field]: value };
		setPackages(updated);
	};

	const formatPrice = (value: string): string => {
		// Remove all non-numeric characters except decimal point
		let numericValue = value.replace(/[^\d.]/g, "");

		// Ensure only one decimal point
		const parts = numericValue.split(".");
		if (parts.length > 2) {
			numericValue = parts[0] + "." + parts.slice(1).join("");
		}

		// If no decimal point, add .00
		if (!numericValue.includes(".")) {
			numericValue += ".00";
		} else {
			// Ensure exactly 2 digits after decimal
			const [whole, decimal] = numericValue.split(".");
			if (decimal) {
				numericValue = whole + "." + decimal.padEnd(2, "0").substring(0, 2);
			} else {
				numericValue += "00";
			}
		}

		return numericValue;
	};

	const handlePriceChange = (index: number, value: string) => {
		const formattedPrice = formatPrice(value);
		updatePackage(index, "price", formattedPrice);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const validPackages = packages.filter(
			(pkg) => pkg.name.trim() && pkg.price.trim(),
		);
		if (validPackages.length === 0) {
			alert("Please add at least one package");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "packages",
						data: {
							packages: validPackages.map((pkg) => ({
								name: pkg.name,
								description: pkg.description,
								price: pkg.price,
								discountPercentage: pkg.discountPercentage,
							})),
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save packages");
			}

			onNext({ packages: validPackages });
		} catch (error) {
			console.error("Error saving packages:", error);
			alert("Failed to save packages. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 7 of 15</span>
					<span className={styles.progressLabel}>Service Packages</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "40%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Package className={styles.cardIcon} />
						Service Packages
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Create service packages that families can purchase. Define your
						offerings and pricing.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{packages.map((pkg, index) => (
							<motion.div
								key={index}
								className={styles.itemCard}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3, delay: index * 0.1 }}
							>
								{packages.length > 1 && (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => removePackage(index)}
										className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
									>
										<X className="h-4 w-4" />
									</Button>
								)}
								<div className="space-y-4">
									<Input
										placeholder="Package Name (e.g., Basic Support, Full Service)"
										value={pkg.name}
										onChange={(e) => updatePackage(index, "name", e.target.value)}
										className="font-karla"
										required
									/>
									<Textarea
										placeholder="Describe what's included in this package"
										value={pkg.description}
										onChange={(e) =>
											updatePackage(index, "description", e.target.value)
										}
										rows={3}
										className="font-karla"
									/>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className={`block mb-1 ${styles.fieldLabel}`}>
												Price ($)
											</label>
											<Input
												type="number"
												placeholder="0.00"
												value={pkg.price}
												onChange={(e) => handlePriceChange(index, e.target.value)}
												className="font-karla"
												required
											/>
										</div>
										<div>
											<label className={`block mb-1 ${styles.fieldLabel}`}>
												Discount (%)
											</label>
											<Input
												type="number"
												placeholder="0"
												value={pkg.discountPercentage}
												onChange={(e) =>
													updatePackage(index, "discountPercentage", e.target.value)
												}
												className="font-karla"
											/>
										</div>
									</div>
								</div>
							</motion.div>
						))}

						<Button
							type="button"
							variant="outline"
							onClick={addPackage}
							className={styles.addButton}
						>
							<Plus className="h-4 w-4 mr-2" />
							Add Another Package
						</Button>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
